import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useGetProofupUrlMutation } from "../../../store/services/proofupApi";
import { useChildPopup } from "../../../shared/utilities/usePopup";
import { useTranslation } from "react-i18next";
import { ProofupHelpVideo } from "src/components/Media/ProofupHelpVideo/ProofupHelpVideo";

interface IncomeVerificationWidgetProps {
  value?: any;
  onChange?: (value: any) => void;
  formData?: any;
  options?: any;
}

export const IncomeVerificationWidget: React.FC<
  IncomeVerificationWidgetProps
> = ({ value, onChange, formData, options = {} }) => {
  const personId = options.personId;
  const leaseId = options.leaseId;
  const propertyId = options.propertyId;
  const [error, setError] = useState<string>("");
  const [proofupAppUrl, setProofupAppUrl] = useState<string>("");
  const { openPopup, status, isLoading } = useChildPopup();

  const { t } = useTranslation();

  const [getProofupUrl] = useGetProofupUrlMutation();

  // Check if verification was already completed
  const isAlreadyCompleted = value === "completed";

  const fetchProofupUrl = React.useCallback(async () => {
    try {
      const proofupUrl = await getProofupUrl({
        leaseId,
        personId,
        propertyId,
      }).unwrap();
      const link = proofupUrl.data.link;
      setProofupAppUrl(link);
    } catch (err) {
      setError("Failed to load income verification. Please try again.");
    }
  }, [leaseId, personId, propertyId, getProofupUrl]);

  useEffect(() => {
    if (leaseId) {
      fetchProofupUrl();
      setError("");
    } else {
      setError("Invalid lease ID. Please go back and try again.");
    }
  }, [leaseId, fetchProofupUrl]);

  // When verification completes, persist completion status to the form
  useEffect(() => {
    if (status === "success" && onChange) {
      onChange("completed");
    }
  }, [status, onChange]);

  const handleVerificationClick = () => {
    if (status === "success" || isAlreadyCompleted) {
      // Mark as completed
      if (onChange) {
        onChange("completed");
      }
    } else if (proofupAppUrl) {
      openPopup(proofupAppUrl);
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      {isAlreadyCompleted ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t(
            "account.incomeVerification.alreadyCompleted",
            "Income verification has already been completed. You can proceed to the next step."
          )}
        </Alert>
      ) : (
        <>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t(
              "account.incomeVerification.partner",
              "We partner with Proofup to provide a secure and efficient income verification process."
            )}
            {t(
              "account.incomeVerification.instruction",
              "Please verify your income using the secure verification process below."
            )}
          </Typography>

          <ProofupHelpVideo />

          <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: "medium", mb: 1 }}>
              {t("account.incomeVerification.whatYouNeedTitle")}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>{t("account.incomeVerification.needItem1")}</li>
              <li>{t("account.incomeVerification.needItem2")}</li>
              <li>{t("account.incomeVerification.needItem3")}</li>
            </ul>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
        </>
      )}

      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleVerificationClick}
          disabled={isLoading || !proofupAppUrl}
          fullWidth
        >
          {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
          {isAlreadyCompleted &&
            t(
              "account.incomeVerification.verifiedButton",
              "Verification Completed"
            )}
          {!isAlreadyCompleted &&
            status === "opening" &&
            t("account.incomeVerification.openingButton")}
          {!isAlreadyCompleted &&
            status === "in_progress" &&
            t("account.incomeVerification.waitingButton")}
          {!isAlreadyCompleted &&
            status === "idle" &&
            t("account.incomeVerification.startButton")}
          {!isAlreadyCompleted &&
            status === "success" &&
            t(
              "account.incomeVerification.verifiedButton",
              "Verification Completed"
            )}
          {!isAlreadyCompleted &&
            status === "error" &&
            t("account.incomeVerification.retryButton", "Retry Verification")}
          {!isAlreadyCompleted &&
            status === "closed" &&
            t("account.incomeVerification.retryButton", "Retry Verification")}
        </Button>
      </Box>

      {status === "success" && !isAlreadyCompleted && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t(
            "account.incomeVerification.successMessage",
            "Income verification completed successfully. You can proceed to the next step."
          )}
        </Alert>
      )}

      <Typography variant="body2" sx={{ mt: 3, color: "text.secondary" }}>
        {t("account.incomeVerification.securityNote")}
      </Typography>
    </Box>
  );
};
